import { Check, Email, LinkedIn, Star } from '@mui/icons-material';
import { twMerge } from '@sortlist-frontend/design-system';
import { TFunction, useTranslation } from '@sortlist-frontend/translation/ssr';

import { homeConfig } from '_features/home/home.config';

type PillarType = 'signals' | 'marketplace' | 'outbound';

interface PillarSectionProps {
  type: PillarType;
  imagePosition?: 'left' | 'right';
}

const COMPANY_LOGOS: Record<string, string> = {
  Spotify: '/static/images/logos/spotify.svg',
  BMW: '/static/images/logos/bmw.svg',
  Zalando: '/static/images/logos/zalando.svg',
  Samsung: '/static/images/logos/samsung.svg',
};

interface SignalCardProps {
  company: string;
  message: string;
  detail: string;
}

const SignalCard = ({ company, message, detail }: SignalCardProps) => {
  const logoUrl = COMPANY_LOGOS[company];

  return (
    <div className="bg-white rounded-xl p-4 flex items-center gap-4 shadow-md">
      <div className="w-8 h-8 flex items-center justify-center flex-shrink-0">
        {logoUrl ? (
          <img src={logoUrl} alt={company} className="w-6 h-6 object-contain" />
        ) : (
          <span className="text-xs font-bold text-secondary-700">{company.charAt(0)}</span>
        )}
      </div>
      <div className="min-w-0">
        <p className="text-sm font-bold text-secondary-950 truncate">{message}</p>
        <p className="text-xs text-secondary-500">{detail}</p>
      </div>
    </div>
  );
};

const SignalsVisual = () => {
  const { t } = useTranslation(homeConfig.i18nNamespaces);

  return (
    <div className="flex flex-col gap-3">
      <SignalCard
        company="Spotify"
        message={t('home:sections.pillar.signals.visual.spotifyMessage', { company: 'Spotify' })}
        detail={t('home:sections.pillar.signals.visual.spotifyDetail')}
      />
      <SignalCard
        company="BMW"
        message={t('home:sections.pillar.signals.visual.bmwMessage', { company: 'BMW' })}
        detail={t('home:sections.pillar.signals.visual.bmwDetail')}
      />
      <SignalCard
        company="Zalando"
        message={t('home:sections.pillar.signals.visual.zalandoMessage', { company: 'Zalando' })}
        detail={t('home:sections.pillar.signals.visual.zalandoDetail')}
      />
      <SignalCard
        company="Samsung"
        message={t('home:sections.pillar.signals.visual.samsungMessage', { company: 'Samsung' })}
        detail={t('home:sections.pillar.signals.visual.samsungDetail')}
      />
    </div>
  );
};

const getInitials = (name: string) => {
  const words = name.split(' ');
  if (words.length >= 2) {
    return `${words[0].charAt(0)}${words[1].charAt(0)}`.toUpperCase();
  }
  return name.substring(0, 2).toUpperCase();
};

interface AgencyCardProps {
  name: string;
  location: string;
  rating: number;
  reviews: number;
  promotedLabel?: string;
  avatarBgClass: string;
  avatarTextClass: string;
}

const AgencyCard = ({
  name,
  location,
  rating,
  reviews,
  promotedLabel,
  avatarBgClass,
  avatarTextClass,
}: AgencyCardProps) => (
  <div className="bg-white rounded-xl p-4 flex items-center gap-3 shadow-md">
    <div className={twMerge('w-10 h-10 rounded-lg flex-shrink-0 flex items-center justify-center', avatarBgClass)}>
      <span className={twMerge('text-xs font-bold', avatarTextClass)}>{getInitials(name)}</span>
    </div>
    <div className="flex-1 min-w-0">
      <div className="flex items-center gap-2">
        <p className="text-sm font-bold text-secondary-950">{name}</p>
        {promotedLabel && (
          <span className="text-[10px] font-medium bg-primary-100 text-primary-500 px-2 py-0.5 rounded">
            {promotedLabel}
          </span>
        )}
      </div>
      <p className="text-xs text-secondary-500">{location}</p>
    </div>
    <div className="flex items-center gap-1 text-xs">
      <Star className="text-yellow-500" style={{ fontSize: '14px' }} />
      <span className="font-medium text-secondary-950">{rating}</span>
      <span className="text-secondary-400">({reviews})</span>
    </div>
  </div>
);

const MarketplaceVisual = () => {
  const { t } = useTranslation(homeConfig.i18nNamespaces);

  return (
    <div className="flex flex-col gap-3 min-w-[320px]">
      <div className="bg-white rounded-lg px-4 py-3 flex items-center gap-2 text-secondary-400 text-sm shadow-md">
        <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <circle cx="11" cy="11" r="8" strokeWidth="2" />
          <path d="M21 21l-4.35-4.35" strokeWidth="2" strokeLinecap="round" />
        </svg>
        <span className="whitespace-nowrap">{t('home:sections.pillar.marketplace.visual.searchPlaceholder')}</span>
      </div>
      <AgencyCard
        name={t('home:sections.pillar.marketplace.visual.agency1Name')}
        location={t('home:sections.pillar.marketplace.visual.agency1Location')}
        rating={4.9}
        reviews={127}
        promotedLabel={t('home:sections.pillar.marketplace.visual.promoted')}
        avatarBgClass="bg-primary-100"
        avatarTextClass="text-primary-500"
      />
      <AgencyCard
        name={t('home:sections.pillar.marketplace.visual.agency2Name')}
        location={t('home:sections.pillar.marketplace.visual.agency2Location')}
        rating={4.8}
        reviews={89}
        avatarBgClass="bg-success-200"
        avatarTextClass="text-success-500"
      />
      <AgencyCard
        name={t('home:sections.pillar.marketplace.visual.agency3Name')}
        location={t('home:sections.pillar.marketplace.visual.agency3Location')}
        rating={4.7}
        reviews={156}
        avatarBgClass="bg-danger-200"
        avatarTextClass="text-danger-500"
      />
    </div>
  );
};

const OutboundVisual = () => {
  const { t } = useTranslation(homeConfig.i18nNamespaces);

  return (
    <div className="flex flex-col gap-3">
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center">
            <Email className="text-primary-500" style={{ fontSize: '14px' }} />
          </div>
          <span className="text-xs font-medium text-secondary-500">
            {t('home:sections.pillar.outbound.visual.step1Label')}
          </span>
        </div>
        <p className="text-sm text-secondary-700">
          {t('home:sections.pillar.outbound.visual.step1Message', { name: 'Sarah', company: 'TechCorp' })}
        </p>
      </div>
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center">
            <LinkedIn className="text-primary-500" style={{ fontSize: '14px' }} />
          </div>
          <span className="text-xs font-medium text-secondary-500">
            {t('home:sections.pillar.outbound.visual.step2Label')}
          </span>
        </div>
        <p className="text-sm text-secondary-700">{t('home:sections.pillar.outbound.visual.step2Message')}</p>
      </div>
      <div className="bg-white rounded-xl p-4 shadow-md">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center">
            <Email className="text-primary-500" style={{ fontSize: '14px' }} />
          </div>
          <span className="text-xs font-medium text-secondary-500">
            {t('home:sections.pillar.outbound.visual.step3Label')}
          </span>
        </div>
        <p className="text-sm text-secondary-700">{t('home:sections.pillar.outbound.visual.step3Message')}</p>
      </div>
    </div>
  );
};

const PILLAR_VISUALS: Record<PillarType, () => JSX.Element> = {
  signals: SignalsVisual,
  marketplace: MarketplaceVisual,
  outbound: OutboundVisual,
};

const getPillarBadge = (t: TFunction, type: PillarType): string => {
  switch (type) {
    case 'signals':
      return t('home:sections.pillar.signals.badge');
    case 'marketplace':
      return t('home:sections.pillar.marketplace.badge');
    case 'outbound':
      return t('home:sections.pillar.outbound.badge');
  }
};

const getPillarTitle = (t: TFunction, type: PillarType): string => {
  switch (type) {
    case 'signals':
      return t('home:sections.pillar.signals.title');
    case 'marketplace':
      return t('home:sections.pillar.marketplace.title');
    case 'outbound':
      return t('home:sections.pillar.outbound.title');
  }
};

const getPillarTitleEmphasis = (t: TFunction, type: PillarType): string => {
  switch (type) {
    case 'signals':
      return t('home:sections.pillar.signals.titleEmphasis');
    case 'marketplace':
      return t('home:sections.pillar.marketplace.titleEmphasis');
    case 'outbound':
      return t('home:sections.pillar.outbound.titleEmphasis');
  }
};

const getPillarDescription = (t: TFunction, type: PillarType): string => {
  switch (type) {
    case 'signals':
      return t('home:sections.pillar.signals.description');
    case 'marketplace':
      return t('home:sections.pillar.marketplace.description');
    case 'outbound':
      return t('home:sections.pillar.outbound.description');
  }
};

const getPillarResult = (t: TFunction, type: PillarType): string => {
  switch (type) {
    case 'signals':
      return t('home:sections.pillar.signals.result');
    case 'marketplace':
      return t('home:sections.pillar.marketplace.result');
    case 'outbound':
      return t('home:sections.pillar.outbound.result');
  }
};

const getPillarFeatures = (t: TFunction, type: PillarType): string[] => {
  switch (type) {
    case 'signals':
      return [
        t('home:sections.pillar.signals.feature1'),
        t('home:sections.pillar.signals.feature2'),
        t('home:sections.pillar.signals.feature3'),
        t('home:sections.pillar.signals.feature4'),
      ];
    case 'marketplace':
      return [
        t('home:sections.pillar.marketplace.feature1'),
        t('home:sections.pillar.marketplace.feature2'),
        t('home:sections.pillar.marketplace.feature3'),
        t('home:sections.pillar.marketplace.feature4'),
      ];
    case 'outbound':
      return [
        t('home:sections.pillar.outbound.feature1'),
        t('home:sections.pillar.outbound.feature2'),
        t('home:sections.pillar.outbound.feature3'),
        t('home:sections.pillar.outbound.feature4'),
      ];
  }
};

const PILLAR_BADGE_CLASSES: Record<PillarType, string> = {
  signals: 'bg-danger-200 text-danger-500',
  marketplace: 'bg-primary-100 text-primary-500',
  outbound: 'bg-success-200 text-success-500',
};

const PILLAR_RESULT_CLASSES: Record<PillarType, string> = {
  signals: 'bg-success-200 text-success-500',
  marketplace: 'bg-primary-100 text-primary-500',
  outbound: 'bg-success-200 text-success-500',
};

export const PillarSection = ({ type, imagePosition = 'left' }: PillarSectionProps) => {
  const { t } = useTranslation(homeConfig.i18nNamespaces);
  const features = getPillarFeatures(t, type);

  const contentBlock = (
    <div className="flex flex-col justify-center">
      <span
        className={twMerge(
          'inline-block text-sm font-medium px-3 py-1 rounded-full w-fit mb-6',
          PILLAR_BADGE_CLASSES[type],
        )}>
        {getPillarBadge(t, type)}
      </span>

      <h2 className="font-serif text-3xl md:text-4xl font-bold text-secondary-950 mb-6 leading-tight tracking-tight">
        {getPillarTitle(t, type)} <span className="text-primary-600">{getPillarTitleEmphasis(t, type)}</span>
        <span className="text-primary-600 font-serif">.</span>
      </h2>

      <p className="text-lg text-secondary-600 mb-8 leading-relaxed">{getPillarDescription(t, type)}</p>

      <ul className="space-y-4 mb-8">
        {features.map((feature, index) => (
          <li key={index} className="flex items-start gap-3">
            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-success-500 text-white flex items-center justify-center mt-0.5">
              <Check className="text-sm" style={{ fontSize: '14px' }} />
            </span>
            <span className="text-secondary-700">{feature}</span>
          </li>
        ))}
      </ul>

      <div className={twMerge('text-sm font-medium px-4 py-3 rounded-lg w-fit', PILLAR_RESULT_CLASSES[type])}>
        {getPillarResult(t, type)}
      </div>
    </div>
  );

  const VisualComponent = PILLAR_VISUALS[type];

  return (
    <section className="py-24 bg-white">
      <div className="container-lg px-6">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          <div className={twMerge('order-1', imagePosition === 'left' ? 'lg:order-2' : 'lg:order-1')}>
            {contentBlock}
          </div>
          <div
            className={twMerge(
              'order-2 bg-secondary-100 rounded-2xl p-6 md:p-8 overflow-hidden',
              imagePosition === 'left' ? 'lg:order-1' : 'lg:order-2',
            )}>
            <VisualComponent />
          </div>
        </div>
      </div>
    </section>
  );
};
